import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import "../../styles/VendorReviewView.css";
import GloablButton from "../GloablButton";
import RequestIcon from "../../icons/RequestIcon";
import RejectedIconXwhite from "../../icons/RejectedIconXwhite";
import ApprovedIconWhite from "../../icons/Arrows/approvedIconWhite";
import ApproveModal from "./ApproveModal";
import RejectModal from "./RejectModal";
import RequestDocsModal from "./RequestDocsModal";
import companyController from "../../controllers/companyController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent) || "";

const firstDifferentFrom = (blockedValue, ...values) =>
  values.find((value) => isPresent(value) && value !== blockedValue) || "";

const NON_RESUBMISSION_REASON_STATUSES = new Set([
  "DEFAULT",
  "PENDING",
  "APPROVED",
  "VERIFIED",
]);

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload;
};

const QR_CODE_KEYS = new Set([
  "companyqrcode",
  "companyqr",
  "qrcode",
  "qrcodebase64",
  "codevalue",
]);

const getCompanyQrCodeFromPayload = (payload) => {
  if (!payload) return "";

  if (typeof payload === "string") return payload.trim();

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const qrCode = getCompanyQrCodeFromPayload(item);
      if (qrCode) return qrCode;
    }

    return "";
  }

  if (typeof payload !== "object") return "";

  for (const [key, value] of Object.entries(payload)) {
    const normalizedKey = key.replace(/[^a-z0-9]/gi, "").toLowerCase();

    if (QR_CODE_KEYS.has(normalizedKey)) {
      const qrCode = String(value || "").trim();
      if (qrCode) return qrCode;
    }
  }

  for (const value of Object.values(payload)) {
    const qrCode = getCompanyQrCodeFromPayload(value);
    if (qrCode) return qrCode;
  }

  return "";
};

const getQrImageUri = async (qrCode) => {
  const normalized = String(qrCode || "").trim();

  if (!normalized) return "";
  if (normalized.startsWith("data:image")) return normalized;
  if (/^https?:\/\/.+\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(normalized)) {
    return normalized;
  }
  if (/^https?:\/\//i.test(normalized)) {
    return QRCode.toDataURL(normalized, {
      errorCorrectionLevel: "H",
      margin: 1,
      scale: 8,
    });
  }

  return `data:image/png;base64,${normalized.replace(
    /^data:[^;]+;base64,/i,
    "",
  )}`;
};

const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const toJavaTimestamp = (value = new Date()) => {
  const date = new Date(value);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const pad = (number) => String(number).padStart(2, "0");

  return `${safeDate.getFullYear()}-${pad(safeDate.getMonth() + 1)}-${pad(
    safeDate.getDate(),
  )} ${pad(safeDate.getHours())}:${pad(safeDate.getMinutes())}:${pad(
    safeDate.getSeconds(),
  )}`;
};

const getBusinessTypePayload = (row = {}) => {
  const rawType = firstDefined(
    row.category,
    row.businessName,
    row.businessTypeLabel,
    row.businessProfileTypeName,
    row.businessType,
  );
  const normalized = String(rawType || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase();

  if (normalized.includes("restaurant") || normalized.includes("cuisine")) {
    return "CUISINE_TYPES";
  }

  if (normalized.includes("grocery") || normalized.includes("grocer")) {
    return "GROCERIES";
  }

  return rawType || "CUISINE_TYPES";
};

const toDisplayLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getResubmissionReasonRows = (attachmentReasons = []) =>
  attachmentReasons.filter(
    (attachmentReason) =>
      attachmentReason?.attachmentID &&
      attachmentReason?.attachmentStatus &&
      !NON_RESUBMISSION_REASON_STATUSES.has(
        normalizeStatus(attachmentReason.attachmentStatus),
      ),
  );

const getAttachmentReasonsMap = (attachmentReasons = []) =>
  getResubmissionReasonRows(attachmentReasons).reduce(
    (reasonsMap, attachmentReason) => ({
      ...reasonsMap,
      [attachmentReason.attachmentType || attachmentReason.attachmentID]:
        normalizeStatus(attachmentReason.attachmentStatus),
    }),
    {},
  );

const getResubmissionReasonMessage = (attachmentReasons = []) => {
  const reasonRows = getResubmissionReasonRows(attachmentReasons);

  if (reasonRows.length === 0) return "";

  return [
    "Document resubmission reasons:",
    ...reasonRows.map(
      (reason) =>
        `- ${reason.documentName || toDisplayLabel(reason.attachmentType)}: ${toDisplayLabel(
          reason.attachmentStatus,
        )}`,
    ),
  ].join("\n");
};

const mergeMessageWithReasons = (message, reasonMessage) =>
  [String(message || "").trim(), reasonMessage].filter(Boolean).join("\n\n");

const getAttachmentReasonsPayload = (
  row = {},
  companyStatus,
  attachmentReasons = [],
) => {
  const selectedAttachmentReasons = getAttachmentReasonsMap(attachmentReasons);

  if (Object.keys(selectedAttachmentReasons).length > 0) {
    return selectedAttachmentReasons;
  }

  if (
    row.companyAttachmentReasons &&
    Object.keys(row.companyAttachmentReasons).length > 0
  ) {
    return row.companyAttachmentReasons;
  }

  if (companyStatus === "RESUBMIT") {
    return {
      PROOF_OF_LEAGALITY: "INCOMPLETE_INFORMATION",
    };
  }

  return {};
};

const getStatusPayload = (
  row,
  companyStatus,
  messageToApplicant = "",
  attachmentReasons = [],
) => {
  const companyID = firstDefined(row.companyID, row.companyId, row.company_id);
  const requestId =
    firstDifferentFrom(
      companyID,
      row.requestId,
      row.requestID,
      row.applicationId,
      row.applicationID,
      row.companyRequestId,
      row.companyRequestID,
      row.companyRequest?.requestId,
      row.companyRequest?.requestID,
      row.companyRequest?.id,
      row.request?.requestId,
      row.request?.requestID,
      row.request?.id,
      row.id,
    ) || companyID;

  return {
    businessType: getBusinessTypePayload(row),
    companyAttachmentReasons: getAttachmentReasonsPayload(
      row,
      companyStatus,
      attachmentReasons,
    ),
    companyID,
    companyStatus,
    messageToApplicant,
    ownerId: firstDefined(
      row.ownerId,
      row.ownerID,
      row.ownerUserId,
      row.userId,
      row.userID,
    ),
    requestId,
    requestType: row.requestType || "VENDOR",
    url: row.url || window.location.origin,
    vendorName: firstDefined(row.vendorName, row.companyName, row.storeName),
  };
};

const getUpdateDetailsPayload = (
  row,
  status,
  messageToApplicant = "",
  attachmentReasons = [],
) => {
  const payload = {
    activityHistory: toJavaTimestamp(),
    applicationInfo: toJavaTimestamp(
      firstDefined(row.applicationInfo, row.submissionDate, row.submitted),
    ),
    companyID: firstDefined(row.companyID, row.companyId, row.company_id),
    reviewer: userService.getUserData().UserID,
    status,
  };

  if (status !== "APPROVED") {
    payload.companyAttachmentReasons = getAttachmentReasonsPayload(
      row,
      status,
      attachmentReasons,
    );
    payload.messageToApplicant = messageToApplicant;
  }

  return payload;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

function VendorReviewActions({
  attachmentReasons = [],
  row = {},
  onStatusUpdated,
}) {
  useTranslationSnapshot();
  const submitted = formatDateTime(row.submissionDate || row.submitted);
  const reviewerName = row.companyLogs?.reviewerName || "No reviewer assigned";
  const lastActivity = formatDateTime(
    row.companyLogs?.lastActivity || row.companyLogs?.assginedAt,
  );
  const [modal, setModal] = useState(null);
  const [actionError, setActionError] = useState("");
  const [submittingAction, setSubmittingAction] = useState("");
  const [customerQrImageUri, setCustomerQrImageUri] = useState("");
  const [driverQrImageUri, setDriverQrImageUri] = useState("");
  const [qrError, setQrError] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const isApproved =
    normalizeStatus(row.companyStatus || row.requestStatus || row.status) ===
    "APPROVED";
  const isRejected =
    normalizeStatus(row.companyStatus || row.requestStatus || row.status) ===
    "REJECTED";
  const rejectionReason = firstDefined(
    row.messageToApplicant,
    row.rejectionReason,
    row.rejectReason,
    row.reason,
    row.companyLogs?.messageToApplicant,
    row.companyLogs?.rejectionReason,
  );
  const companyId = firstDefined(row.companyID, row.companyId, row.company_id);
  const vendorName = firstDefined(
    row.vendorName,
    row.companyName,
    row.storeName,
  );

  useEffect(() => {
    if (!isApproved) return undefined;

    let isCurrentRequest = true;

    const loadQrCodes = async () => {
      setQrLoading(true);
      setQrError("");

      try {
        let customerQr = "";
        let driverQr = "";

        if (companyId) {
          const response = await companyController.GetCompanyQrCode({
            companyId,
            jwt: getStoredToken(),
          });

          if (!(response?.isAxiosError || response?.response)) {
            const payload = getPayload(response);

            customerQr = getCompanyQrCodeFromPayload(payload);
            driverQr =
              response?.data?.menuCodeValue || payload?.menuCodeValue || "";
          }
        }

        const customerQrImage = customerQr
          ? await getQrImageUri(customerQr)
          : "";
        const driverQrImage = driverQr ? await getQrImageUri(driverQr) : "";

        if (!isCurrentRequest) return;

        setCustomerQrImageUri(customerQrImage);
        setDriverQrImageUri(driverQrImage);
      } catch {
        if (!isCurrentRequest) return;

        setCustomerQrImageUri("");
        setDriverQrImageUri("");
        setQrError("QR codes are not available yet.");
      } finally {
        if (isCurrentRequest) setQrLoading(false);
      }
    };

    loadQrCodes();

    return () => {
      isCurrentRequest = false;
    };
  }, [companyId, isApproved, vendorName]);

  const closeModal = () => {
    if (submittingAction) return;
    setActionError("");
    setModal(null);
  };

  const openModal = (nextModal) => {
    setActionError("");
    setModal(nextModal);
  };

  const submitAction = async (action, message = "") => {
    const reasonMessage =
      action === "request"
        ? getResubmissionReasonMessage(attachmentReasons)
        : "";
    const trimmedMessage = mergeMessageWithReasons(message, reasonMessage);

    if ((action === "reject" || action === "request") && !trimmedMessage) {
      setActionError("Message is required.");
      return;
    }

    if (action === "request" && !reasonMessage) {
      setActionError(
        "Select at least one document resubmission reason before sending.",
      );
      return;
    }

    const config = {
      approve: {
        status: "APPROVED",
        submit: companyController.sendapprovedemail,
      },
      reject: {
        status: "REJECTED",
        submit: companyController.sendrejectedemail,
      },
      request: {
        status: "RESUBMIT",
        submit: companyController.sendResubmissionEmail,
      },
    }[action];

    if (!config) return;

    setSubmittingAction(action);
    setActionError("");

    try {
      const response = await config.submit({
        statusPayload: getStatusPayload(
          row,
          config.status,
          trimmedMessage,
          attachmentReasons,
        ),
        jwt: getStoredToken(),
      });

      if (
        response instanceof Error ||
        response?.isAxiosError ||
        response?.response
      ) {
        throw response;
      }

      const statusResponse = await companyController.updatecompanydetails({
        updateDetails: getUpdateDetailsPayload(
          row,
          config.status,
          trimmedMessage,
          attachmentReasons,
        ),
        jwt: getStoredToken(),
      });

      if (
        statusResponse instanceof Error ||
        statusResponse?.isAxiosError ||
        statusResponse?.response
      ) {
        throw statusResponse;
      }

      setModal(null);
      onStatusUpdated?.(row, config.status, trimmedMessage);
    } catch (requestError) {
      setActionError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to send vendor status email.",
      );
    } finally {
      setSubmittingAction("");
    }
  };

  return (
    <div className="vendor-review-view-right">
      {isApproved ? (
        <>
          <QrReviewCard
            imageUri={customerQrImageUri}
            isLoading={qrLoading}
            title={translateLabel("txt_admin_qr_code", "QR Code")}
          />
          <QrReviewCard
            imageUri={driverQrImageUri}
            isLoading={qrLoading}
            title="Menu QR"
          />
          {qrError && (
            <div className="vendor-review-view-qr-error">{qrError}</div>
          )}
        </>
      ) : isRejected ? (
        <div className="vendor-review-view-status-card vendor-review-view-status-card-rejected">
          <div className="vendor-review-view-status-title">Vendor Rejected</div>
          <div className="vendor-review-view-status-copy">
            This application has been rejected.
          </div>
          {rejectionReason && (
            <div className="vendor-review-view-status-reason">
              <span>{translateLabel("txt_admin_reason", "Reason")}</span>
              <p>{rejectionReason}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="vendor-review-view-actions-card">
          <div className="vendor-review-view-actions-title">{translateLabel("txt_common_actions", "Actions")}</div>

          <GloablButton
            width="240px"
            height="42px"
            backgroundColor="#9C9C9C"
            text={translateLabel("txt_admin_approve_vendor", "Approve Vendor")}
            borderRadius="8px"
            color="#FFFFFF"
            padding="10px"
            icon={<ApprovedIconWhite />}
            iconPosition="left"
            onClick={() => openModal("approve")}
          />

          <GloablButton
            width="240px"
            height="49px"
            background="linear-gradient(0deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2)), #FF3C3C"
            text={translateLabel("txt_admin_reject_vendor", "Reject Vendor")}
            borderRadius="8px"
            color="#FFFFFF"
            padding="10px"
            icon={<RejectedIconXwhite />}
            iconPosition="left"
            onClick={() => openModal("reject")}
          />

          <GloablButton
            width="240px"
            height="50px"
            backgroundColor="#FFFFFF"
            text={translateLabel("txt_admin_request_documentations", "Request Documentations")}
            borderRadius="8px"
            color="#000000"
            padding="10px"
            boxShadow="inset 0 0 0 1px #D3D3D3"
            icon={<RequestIcon />}
            iconPosition="left"
            onClick={() => openModal("request")}
          />
        </div>
      )}

      <div className="vendor-review-view-info-card">
        <div className="vendor-review-view-info-card-title">
          Application Info
        </div>
        <div className="vendor-review-view-info-label">Submitted</div>
        <div className="vendor-review-view-info-value">{submitted}</div>
      </div>

      <div className="vendor-review-view-info-card">
        <div className="vendor-review-view-info-card-title">
          Activity History
        </div>
        <div className="vendor-review-view-info-label">{reviewerName}</div>
        <div className="vendor-review-view-info-value">{lastActivity}</div>
      </div>

      {modal === "approve" && (
        <ApproveModal
          error={actionError}
          isSubmitting={submittingAction === "approve"}
          onClose={closeModal}
          onConfirm={() => submitAction("approve")}
        />
      )}
      {modal === "reject" && (
        <RejectModal
          error={actionError}
          isSubmitting={submittingAction === "reject"}
          onClose={closeModal}
          onConfirm={(reason) => submitAction("reject", reason)}
        />
      )}
      {modal === "request" && (
        <RequestDocsModal
          error={actionError}
          isSubmitting={submittingAction === "request"}
          reasonMessage={getResubmissionReasonMessage(attachmentReasons)}
          onClose={closeModal}
          onConfirm={(message) => submitAction("request", message)}
        />
      )}
    </div>
  );
}

function QrReviewCard({ imageUri, isLoading, title }) {
  useTranslationSnapshot();
  const downloadQr = () => {
    if (!imageUri) return;

    const link = document.createElement("a");
    link.href = imageUri;
    link.download = `${title.toLowerCase().replace(/\s+/g, "-")}.png`;
    link.click();
  };

  const printQr = () => {
    if (!imageUri) return;

    const printWindow = window.open("", "_blank", "width=420,height=520");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:Arial,sans-serif;">
          <div style="text-align:center;">
            <h3>${title}</h3>
            <img src="${imageUri}" alt="${title}" style="width:260px;height:260px;object-fit:contain;" />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="vendor-review-view-qr-card">
      <div className="vendor-review-view-qr-title">{translateText(title)}</div>
      <div className="vendor-review-view-qr-preview">
        {imageUri ? (
          <img src={imageUri} alt={translateText(title)} />
        ) : (
          <span>{isLoading ? translateLabel("txt_admin_loading_qr", "Loading QR...") : translateLabel("txt_admin_qr_unavailable", "QR unavailable")}</span>
        )}
      </div>
      <div className="vendor-review-view-qr-actions">
        <button type="button" disabled={!imageUri} onClick={downloadQr}> {translateLabel("btn_download", "Download")} </button>
        <button type="button" disabled={!imageUri} onClick={printQr}> {translateLabel("btn_print", "Print")} </button>
      </div>
    </div>
  );
}

export default VendorReviewActions;
